import { GUIDE_LINK_REFERRER_PARAM } from '../constants/featureLinks';
import type { GuideTopic } from '../types';

import { resolveGuideArticleRequest } from './resolveGuideArticleRequest';

export const GUIDE_PATH = '/kb';

export function guideArticlePath(slug: string, basePath = GUIDE_PATH): string {
  return `${basePath}/${slug.split('/').map(encodeURIComponent).join('/')}`;
}

function resolveArticleSlug(value: string, topics: GuideTopic[]): string | undefined {
  const request = resolveGuideArticleRequest(value, topics);
  return request ? `${request.topic}/${request.id}` : undefined;
}

export function safeGuideUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  for (const char of value) {
    if (char.charCodeAt(0) <= 32 || char === '\\') return undefined;
  }
  if (/^(?:\/(?!\/)|#|mailto:)/i.test(value)) return value;
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' && !url.username && !url.password) return value;
    if (url.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(url.hostname)) return value;
  } catch {
    /* An unsupported URL is rendered without an active link. */
  }
  return undefined;
}

export function guideLink(
  href: unknown,
  apiOrigin: string,
  topics: GuideTopic[] = [],
  basePath = GUIDE_PATH,
): string | undefined {
  const safe = safeGuideUrl(href);
  if (!safe) return safe;
  if (safe.startsWith('#/')) {
    const target = new URL(safe.slice(1), apiOrigin);
    const slug = resolveArticleSlug(target.pathname.slice(1), topics);
    if (!slug) return undefined;
    const section = target.searchParams.get('section');
    return `${guideArticlePath(slug, basePath)}${section ? `#${encodeURIComponent(section)}` : ''}`;
  }
  if (safe.startsWith('#') || safe.startsWith('mailto:')) return safe;
  const url = new URL(safe, apiOrigin);
  const match =
    /^\/api\/product-guide-v2\/(?:(?:public|personalized)\/)?articles\/([a-z0-9][a-z0-9-]{0,79}(?:\/[a-z0-9][a-z0-9-]{0,79})?)$/.exec(
      url.pathname,
    );
  if (!match || (!safe.startsWith('/') && url.origin !== new URL(apiOrigin).origin)) return safe;
  const slug = resolveArticleSlug(match[1], topics);
  return slug ? `${guideArticlePath(slug, basePath)}${url.hash}` : undefined;
}

export function isHostedVideo(url: string): boolean {
  return (
    /^https:\/\/www\.youtube-nocookie\.com\/embed\/[\w-]{11}$/.test(url) ||
    /^https:\/\/player\.vimeo\.com\/video\/\d+$/.test(url)
  );
}

/** Reader URL for an article (or the guide root), optionally attributed with a referrer. */
export function guideUrl(slug?: string, referrer?: string): string {
  const path = slug ? guideArticlePath(slug) : GUIDE_PATH;
  return referrer ? `${path}?${GUIDE_LINK_REFERRER_PARAM}=${encodeURIComponent(referrer)}` : path;
}
